/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { debounceTime, filter, map } from 'rxjs/operators';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { TranslateService } from '@ngx-translate/core';
import { Site } from 'src/app/model/site';
import { CASpecialPlanning } from 'src/app/model/control-access';
import { DatePipe } from '@angular/common';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectBoardUsersTaskRunning, selectCurrentDeviceStatus, selectSpecialPlanningModify, selectSpecialPlannings, selectSpecialPlanningsMaxData, selectSpecialPlanningsPagination } from 'src/app/store/device/device.selector';
import { ItemsPagination, MtmaMaxData } from 'src/app/model/api/data-rest';
import { TableLazyLoadEvent } from 'primeng/table';
import { pageSortChange } from 'src/app/services/table';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StandardDesignParameters, StandardMtmaLayout, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectDeviceTabIndex } from 'src/app/store/home/home.selector';
import { ControlAccessSpecialPlanningDialogComponent } from '../control-access-special-planning-dialog/control-access-special-planning-dialog.component';
import { SPECIAL_PLANNING_TABLE_HEADER, SPECIAL_PLANNING_PAGE_DEFAULT_SIZE, SPECIAL_PLANNING_DEFAULT_SORT_COLUMN, SPECIAL_PLANNING_DEFAULT_SORT_COLUMN_DIRECTION } from 'src/app/conf/control-access';
import { DeviceStatus } from 'src/app/model/device-status';
import { RbePlusIndexTab } from 'src/app/model/device';

@Component({
	selector: 'app-control-access-special-planning-list',
	templateUrl: './control-access-special-planning-list.component.html',
	providers: [ConfirmationService, DialogService]
})

export class ControlAccessSpecialPlanningListComponent implements OnInit, OnDestroy {
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	specialPlannings$: Observable<CASpecialPlanning[] | undefined>;
	specialPlanningsSub: Subscription | undefined;
	specialPlanningsPagination$: Observable<ItemsPagination | undefined>;
	specialPlanningsPaginationSub: Subscription | undefined;
	specialPlanningsMaxData$: Observable<MtmaMaxData | undefined>;
	specialPlanningsMaxDataSub: Subscription | undefined;
	specialPlanningModify$: Observable<boolean | undefined>;
	specialPlanningModifySub: Subscription | undefined;
	taskRunning$: Observable<boolean>;
	taskRunningSub: Subscription | undefined;
	tabIndex: number;
	tabIndex$: Observable<number | undefined>;
	tabIndexSub: Subscription | undefined;
	deviceStatus: DeviceStatus;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;

	searchFilter: FormControl = new FormControl('');
	filterSub: Subscription;
	filter: '';

	specialPlanningsColumns = SPECIAL_PLANNING_TABLE_HEADER;
	specialPlanningsPagingSub: Subscription;
	specialPlanningsPageSortSub: Subscription;
	specialPlanningsPaging: PaginationParams = {
		size: SPECIAL_PLANNING_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: SPECIAL_PLANNING_DEFAULT_SORT_COLUMN,
		order: SPECIAL_PLANNING_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	specialPlanningsPageSize: number = SPECIAL_PLANNING_PAGE_DEFAULT_SIZE;
	// specialPlanningsPageSizeOptions = SPECIAL_PLANNING_PAGE_SIZE_OPTIONS;
	totalRecords: number;

	specialPlanningsMaxSizeMemorySub: Subscription;
	specialPlanningsMemorySize = 1;
	specialPlanningsMaxMemorySize = 1;

	specialPlanningsLoading: boolean;
	specialPlannings: CASpecialPlanning[];

	currentSite: Site;

	specialPlanningConfirmDeleteSub: Subscription;
	online: boolean;
	taskRunning: boolean;


	dateTransform: DatePipe;
	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	opened = false;

	screenWidth: number;
	MIN_MD_WIDTH = StandardDesignParameters.MAX_MD_WIDTH;

	constructor(
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.site$ = this.store.select(selectCurrentSite);
		this.specialPlannings$ = this.store.select(selectSpecialPlannings);
		this.specialPlanningsPagination$ = this.store.select(selectSpecialPlanningsPagination);
		this.specialPlanningsMaxData$ = this.store.select(selectSpecialPlanningsMaxData);
		this.specialPlanningModify$ = this.store.select(selectSpecialPlanningModify);
		this.tabIndex$ = this.store.select(selectDeviceTabIndex);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.taskRunning$ = this.store.select(selectBoardUsersTaskRunning);
	}

	ngOnInit(): void {
		const currentLang = this.translate.currentLang;

		this.dateTransform = new DatePipe(currentLang);

		this.siteSub = this.site$.subscribe(currentSite => {
			this.currentSite = currentSite;
		});
		this.tabIndexSub = this.tabIndex$?.subscribe(
			async (tabIndex: number) => {
				this.tabIndex = tabIndex;
				this.dispatchGetSpecialPlanningsPaging();
			});

		this.subscribeAll();
		this.onResize();
	}

	subscribeAll(): void {
		this.filterSub = this.searchFilter.valueChanges.pipe(
			debounceTime(500),
			map(v => v.trim().toLowerCase())
		).subscribe(value => {
			this.filter = value;
			this.dispatchGetSpecialPlanningsPaging();
		});
		this.dispatchGetSpecialPlanningsPaging();
		this.specialPlanningsSub = this.specialPlannings$.subscribe(specialPlannings => {
			this.specialPlannings = specialPlannings;
			this.specialPlanningsLoading = false;
		});

		this.specialPlanningsPagingSub = this.specialPlanningsPagination$
			.pipe(filter(p => !!p))
			.subscribe(paging => {
				this.totalRecords = paging.TotalCount;
			});

		this.specialPlanningsMaxSizeMemorySub = this.specialPlanningsMaxData$
			.pipe(filter(p => !!p))
			.subscribe(specialPlanningsMaxData => {
				this.specialPlanningsMemorySize = specialPlanningsMaxData.Current;
				this.specialPlanningsMaxMemorySize = specialPlanningsMaxData.Max;
			});

		this.specialPlanningModifySub = this.specialPlanningModify$
			.pipe(filter(mtmaTimesliceModify => !!mtmaTimesliceModify))
			.subscribe(() => {
				this.dispatchGetSpecialPlanningsPaging();
			});
		this.deviceStatusSub = this.deviceStatus$.subscribe(deviceStatus => {
			this.deviceStatus = deviceStatus;
			if (this.deviceStatus !== undefined) {
				this.online = deviceStatus.Online;
			}
		});
		this.taskRunningSub = this.taskRunning$.subscribe(taskRunning => {
			this.taskRunning = taskRunning;
		});
	}

	ngOnDestroy(): void {
		this.specialPlanningModifySub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.specialPlanningsMaxSizeMemorySub?.unsubscribe();
		this.tabIndexSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.taskRunningSub?.unsubscribe();
	}


	loadSpecialPlannings(event: TableLazyLoadEvent) {
		this.specialPlanningsLoading = true;

		this.specialPlanningsPaging = pageSortChange(this.specialPlanningsPaging, event);

		this.dispatchGetSpecialPlanningsPaging();
	}

	dispatchGetSpecialPlanningsPaging(): void {
		if (this.isSelectTab()) {
			this.store.dispatch(DeviceActions.getSpecialPlannings({
				likeName: this.filter,
				sort: this.specialPlanningsPaging.sort, order: this.specialPlanningsPaging.order, size: this.specialPlanningsPaging.size, page: this.specialPlanningsPaging.page
			}));
		}
	}

	isSelectTab(): boolean {
		return this.tabIndex === RbePlusIndexTab.RBEPLUS_ACCESSCONTROL && this.opened;
	}

	addSpecialPlanning(): void {
		this.store.dispatch(DeviceActions.resetSpecialPlanningForm());
		this.ref = this.dialogService.open(ControlAccessSpecialPlanningDialogComponent, {
			header: this.translate.instant('ADD_SPECIAL_PLANNING'),
			width: '80vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	editSpecialPlanning(specialPlanning: CASpecialPlanning): void {
		this.store.dispatch(DeviceActions.resetSpecialPlanningForm());
		this.ref = this.dialogService.open(ControlAccessSpecialPlanningDialogComponent, {
			header: this.translate.instant('EDIT_SPECIAL_PLANNING'),
			width: '80vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				specialPlanning
			}
		});
	}

	deleteSpecialPlannig(specialPlannings: CASpecialPlanning): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_SPECIAL_PLANNING'),
			message: this.translate.instant('DELETE_SPECIAL_PLANNING_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.deleteSpecialPlanning({ specialPlanningId: specialPlannings.Id }));
			},
			reject: () => {
			}
		});
	}


	getValueMemory(): number {
		return Math.ceil((this.specialPlanningsMemorySize / this.specialPlanningsMaxMemorySize) * 100);
	}

	togglePanel(opened: boolean): void {
		this.opened = opened;
		this.dispatchGetSpecialPlanningsPaging();
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	underTablet(): boolean {
		return StandardPageLayout.underTablet(this.screenWidth);
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-25rem');
	}

	dynamicInputSearchWidth(): string {
		return StandardMtmaLayout.dynamicInputSearchWidth(this.screenWidth, 'w-25rem');
	}
}
